import React, { useEffect, useState, useCallback } from 'react';
import { FiHome, FiHeart, FiRefreshCw, FiSearch, FiAlertCircle, FiMapPin } from 'react-icons/fi';
import { portalAPI } from '../../services/api';

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR')} XOF`;

const TRANSACTION_TYPES = [
  { value: '', label: 'Tous les types' },
  { value: 'sale', label: 'Vente' },
  { value: 'rent', label: 'Location' },
];

const PropertyCard = ({ announcement, isFav, onToggleFav }) => {
  const {
    id,
    title,
    price,
    city,
    property_type,
    transaction_type,
    image_url,
    images,
  } = announcement;

  const imgSrc = image_url || (images && images[0]?.url) || null;

  return (
    <div
      className="card"
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Image / placeholder */}
      <div
        style={{
          height: 180,
          background: imgSrc ? `url(${imgSrc}) center/cover no-repeat` : 'var(--bg-secondary, #f3f4f6)',
          display: imgSrc ? undefined : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 64,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {!imgSrc && <span>🏠</span>}
        <button
          className="btn btn-sm"
          onClick={() => onToggleFav(announcement, isFav)}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            borderRadius: '50%',
            width: 36,
            height: 36,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isFav ? 'var(--primary-color)' : 'rgba(255,255,255,0.9)',
            color: isFav ? '#fff' : 'var(--text-primary)',
            border: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            cursor: 'pointer',
          }}
          title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <FiHeart fill={isFav ? '#fff' : 'none'} />
        </button>
        {transaction_type && (
          <span
            className={`badge badge-${transaction_type === 'sale' ? 'info' : 'success'}`}
            style={{ position: 'absolute', top: 10, left: 10 }}
          >
            {transaction_type === 'sale' ? 'Vente' : 'Location'}
          </span>
        )}
      </div>

      <div className="card-body" style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{title || `Bien #${id}`}</div>
        {city && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
            <FiMapPin size={12} />
            {city}
          </div>
        )}
        {property_type && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8 }}>
            {property_type}
          </div>
        )}
        {price != null && (
          <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--primary-color)' }}>
            {formatCurrency(price)}
          </div>
        )}
      </div>
    </div>
  );
};

const PortalAnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [transactionType, setTransactionType] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [announcementsRes, favRes] = await Promise.all([
        portalAPI.client.listAnnouncements({ limit: 20 }),
        portalAPI.client.listFavorites({}).catch(() => ({ data: [] })),
      ]);

      const items = Array.isArray(announcementsRes.data)
        ? announcementsRes.data
        : announcementsRes.data?.items || announcementsRes.data?.results || [];

      const favData = Array.isArray(favRes.data)
        ? favRes.data
        : favRes.data?.items || [];

      setAnnouncements(items);
      setFavorites(new Set(favData.map((f) => f.announcement_id || f.id)));
    } catch {
      setError('Impossible de charger les annonces.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleToggleFav = async (announcement, isFav) => {
    const id = announcement.id;
    try {
      if (isFav) {
        await portalAPI.client.removeFavorite(id);
        setFavorites((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } else {
        await portalAPI.client.addFavorite({ announcement_id: id });
        setFavorites((prev) => new Set([...prev, id]));
      }
    } catch {
      // silently fail
    }
  };

  const filtered = announcements.filter((a) => {
    const matchSearch =
      !search ||
      (a.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.city || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.property_type || '').toLowerCase().includes(search.toLowerCase());
    const matchType =
      !transactionType || a.transaction_type === transactionType;
    return matchSearch && matchType;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiHome />
            Annonces immobilières
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
            Parcourez les biens disponibles et ajoutez-les à vos favoris.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={fetchAll} disabled={loading}>
          <FiRefreshCw />
          Actualiser
        </button>
      </div>

      {/* Filters */}
      <div
        className="card"
        style={{ marginBottom: 20 }}
      >
        <div
          className="card-body"
          style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <div style={{ flex: '1 1 220px', position: 'relative' }}>
            <FiSearch
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}
            />
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher par titre, ville, type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 34 }}
            />
          </div>
          <div style={{ flex: '0 1 200px' }}>
            <select
              className="form-control"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              {TRANSACTION_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {(search || transactionType) && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setSearch(''); setTransactionType(''); }}
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiAlertCircle size={40} color="var(--danger-color, #ef4444)" />
              <h3>{error}</h3>
              <button className="btn btn-primary" onClick={fetchAll}>
                Réessayer
              </button>
            </div>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiHome size={48} />
              <h3>Aucune annonce trouvée</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                {search || transactionType
                  ? 'Modifiez vos filtres pour voir plus de résultats.'
                  : 'Aucun bien disponible pour le moment.'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
            {filtered.length} annonce{filtered.length > 1 ? 's' : ''} trouvée{filtered.length > 1 ? 's' : ''}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {filtered.map((a) => (
              <PropertyCard
                key={a.id}
                announcement={a}
                isFav={favorites.has(a.id)}
                onToggleFav={handleToggleFav}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PortalAnnouncementsPage;
